import React from 'react';
import useStore from '../../store/useStore';
import type { ApiRingTemplate } from '../../types/api';

/**
 * Profile selector shown at the top of the page.
 * Maps each wedding band ring template to a selectable profile chip.
 * Selecting a chip updates `selectedTemplateId` in the store, which
 * triggers `ExtrudedRingMesh` to re-extrude the ring from the DXF profile.
 */
const ProfileSelector: React.FC = () => {
    const ringTemplates = useStore((s) => s.ringTemplates);
    const selectedTemplateId = useStore((s) => s.selectedTemplateId);
    const setSelectedTemplateId = useStore((s) => s.methods.setSelectedTemplateId);

    if (!ringTemplates.length) return null;

    const getLabel = (t: ApiRingTemplate) =>
        t.ringPartials[0]?.ringProfile?.labelDe ??
        t.ringPartials[0]?.ringProfile?.name ??
        t.nameDe ??
        t.nameEn ??
        t.short;

    const hasDxf = (t: ApiRingTemplate) =>
        (t.ringPartials[0]?.ringProfile?.dxfFiles?.length ?? 0) > 0;

    const available = ringTemplates.filter(hasDxf);

    if (!available.length) return null;

    const activeId = selectedTemplateId ?? available[0].id;

    return (
        <div className="flex flex-wrap justify-center gap-2">
            {available.map((template) => {
                const isActive = template.id === activeId;
                return (
                    <button
                        key={template.id}
                        onClick={() => setSelectedTemplateId(template.id)}
                        className={[
                            'rounded-full px-5 py-2 text-sm tracking-wide font-medium transition-colors',
                            isActive
                                ? 'bg-[var(--fg)] text-[var(--bg)]'
                                : 'bg-transparent border border-[var(--fg-muted)] text-[var(--fg-muted)] hover:border-[var(--fg)] hover:text-[var(--fg)]',
                        ].join(' ')}
                    >
                        {getLabel(template)}
                    </button>
                );
            })}
        </div>
    );
};

export default ProfileSelector;
